import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import NumberFormat, { NumberFormatValues, SourceInfo } from 'react-number-format';
import { TextFieldWrapper } from '@styles/InputStyles';
import { useDebounce } from '@shared/hooks/useDebounce';
import { ITextInputProps, TextInput } from './TextInput';

export interface INumberInputProps extends ITextInputProps {
  shrink?: boolean;
  prefix?: string;
  suffix?: string;
  decimalScale?: number | null;
  allowNegative?: boolean;
  minValue?: number;
  removeThousandSeparator?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  limit?: number;
  onAfterChange?: (value?: number) => void;
}

interface ITarget {
  name: string;
  value: number;
  source: 'prop' | 'event';
  isValueChanged: boolean;
}

export interface IEvent {
  target: ITarget;
}

const NumberInputBase = ({
  value,
  name,
  suffix,
  inputRef,
  decimalScale = 0,
  allowNegative = false,
  limit = 21,
  minValue,
  removeThousandSeparator,
  highlightFullBorder = false,
  onChange,
  onBlur,
  validateOnBlur,
  onAfterChange,
  ...props // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: any) => {
  const [valueOnFocus, setValueOnFocus] = useState<string>();
  const [inputValue, setInputValue] = useState<number | undefined>(value);
  const debouncedInputValue = useDebounce(inputValue, 1000);

  useEffect(
    () => {
      onAfterChange?.(debouncedInputValue);
    },
    [debouncedInputValue]
  );

  const onValueChange = useCallback(({ floatValue }: NumberFormatValues, { source }: SourceInfo) => {
    if (!onChange) return;

    if (source === 'event') {
      setInputValue(floatValue);
    }

    onChange({
      target: {
        name: name,
        value: floatValue,
        source,
      },
    } as IEvent);
  }, [onChange]);

  const valueOnBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!onBlur) return;
      if (validateOnBlur) {
        if (valueOnFocus != event.target.value) {
          onBlur(event);
        }
      } else {
        onBlur(event);
      }
    },
    [valueOnFocus]
  );

  const onValueFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setValueOnFocus(event.target.value);
  }, []);

  const setLimit = useCallback((obj: NumberFormatValues) => {
    const { value } = obj;
    const numericValue = parseFloat(value);
    const isAboveMinValue = typeof minValue === 'number' ? numericValue >= minValue : true;

    return value.toString().length <= limit && isAboveMinValue;
  }, []);

  const decimals = decimalScale || decimalScale === 0 ? decimalScale : suffix === '%' ? 2 : undefined;
  const thousandSeparator = removeThousandSeparator ? '' : true;

  return (
    <TextFieldWrapper highlightFullBorder={highlightFullBorder}>
      <NumberFormat
        {...props}
        ref={inputRef}
        value={value ?? ''}
        name={name}
        suffix={suffix}
        decimalScale={decimals}
        allowNegative={allowNegative}
        fixedDecimalScale={!!decimals}
        thousandSeparator={thousandSeparator}
        customInput={TextInput}
        isAllowed={setLimit}
        onValueChange={onValueChange}
        onBlur={valueOnBlur}
        onFocus={onValueFocus}
      />
    </TextFieldWrapper>
  );
};

export const NumberInput = memo(NumberInputBase);
